import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ProjectService } from 'src/app/core/services/project.service';
import { Group, StackingOptions } from 'src/app/core/types';
import { ColorPipe } from 'src/app/shared/pipes/color.pipe';
import { Subscription } from 'rxjs';
import { Project } from 'src/app/core/objects/project';
import { Message, MessageService } from '../../../../../../../../core/services/message.service';

@Component({
    selector: 'gl-stacking',
    templateUrl: './stacking.component.html',
    styleUrls: ['./stacking.component.scss'],
    standalone: false
})
export class StackingComponent implements OnInit, OnDestroy {
  public form: UntypedFormGroup = this.generateForm();
  public stacking = false;
  public weightOfFloors = false;
  public chooseLoadsToStack = false;
  public specifyPercentageOfSupport = false;
  public loadsGroups: Group[] = [];
  public checkboxLoads = {};
  public stackingLoads: number[] = [];
  private subscriptions: Subscription[] = [];
  @Input() public defaultOptions: StackingOptions;
  @Output() public stackingOptions: EventEmitter<StackingOptions> =
    new EventEmitter<StackingOptions>();
  @Input() public groupData: Group;
  /** Disables stacking toggle and percentage-of-support controls (read-only metadata). */
  @Input() public lockStackingAndSupport = false;

  constructor(
    private projectService: ProjectService,
    private colorPipe: ColorPipe,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
  ) {
    this.projectService.project.subscribe((project: Project): void => {
      project.workSpaces[this.projectService.getCurrentWorkSpace()]?.cargoSpaces[0]?.groups.forEach(
        (group: Group) => this.loadsGroups.push(group),
      );
      if (project.workSpaces[this.projectService.getCurrentWorkSpace()]?.cargoSpaces[1]) {
        project.workSpaces[this.projectService.getCurrentWorkSpace()].cargoSpaces[1].groups.forEach(
          (group: Group) => this.loadsGroups.push(group),
        );
      }
      project.workSpaces[this.projectService.getCurrentWorkSpace()]?.outsideSpace.groups.forEach(
        (group: Group) => this.loadsGroups.push(group),
      );
      this.form.get('load').setValue(this.loadsGroups?.[0]?.id);
    });
  }

  private setMaxWeightCheckboxIfValid(): void {
    this.weightOfFloors =
      this.form.get('maxWeight').valid &&
      this.form.get('maxWeight').value !== undefined &&
      this.form.get('maxWeight').value !== '' &&
      this.form.get('maxWeight').value !== 0;
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      this.messageService.getMessages().subscribe((message: Message): void => {
        if (this.groupData.loadType === 2) {
          if (message.name === 'arrangementChange' && message.data === 0 && this.stacking) {
            this.stacking = false;
            this.setStackingOptions();
            this.cdr.detectChanges();
          } else if (message.name === 'arrangementChange' && message.data !== 0 && !this.stacking) {
            this.stacking = true;
            this.setStackingOptions();
            this.cdr.detectChanges();
          }
        }
      }),
    );
    this.loadsGroups = this.loadsGroups.filter(
      (group: Group): boolean => group.id !== this.groupData.id,
    );
    if (this.defaultOptions) {
      this.stacking = this.defaultOptions.stacking;
      if (this.defaultOptions.maxLoadsOnTop) {
        this.form.controls.stackQuantity.setValue(this.defaultOptions.maxLoadsOnTop);
      }
      if (this.defaultOptions.maxWeightOnTop) {
        this.weightOfFloors = true;
        this.form.controls.maxWeight.setValue(this.defaultOptions.maxWeightOnTop);
      }
      if (this.defaultOptions.boxesBelowAllowed) {
        this.chooseLoadsToStack = true;
        this.defaultOptions.boxesBelowAllowed.forEach((id: number): void => {
          this.checkboxLoads[id] = true;
        });
        this.stackingLoads = [...this.defaultOptions.boxesBelowAllowed];
      }
      if (this.defaultOptions.pctSupport) {
        this.specifyPercentageOfSupport = true;
        this.form.controls.supportPercentage.setValue(this.defaultOptions.pctSupport);
      }
      if (this.defaultOptions.alongFloor) {
        this.form.controls.alongFloor.setValue(this.defaultOptions.alongFloor);
      }
    }
    this.subscriptions.push(
      this.form.controls.stackQuantity.valueChanges.subscribe(() => this.setStackingOptions()),
    );
    this.subscriptions.push(
      this.form.controls.maxWeight.valueChanges.subscribe(() => {
        this.setMaxWeightCheckboxIfValid();
        this.setStackingOptions();
      }),
    );
    this.subscriptions.push(
      this.form.controls.supportPercentage.valueChanges.subscribe((): void => {
        this.setPercentageCheckbox();
        this.setStackingOptions();
      }),
    );
    this.subscriptions.push(
      this.form.controls.alongFloor.valueChanges.subscribe(() => this.setStackingOptions()),
    );
  }

  public setStackGroup(id: number): void {
    if (this.stackingLoads.find((idLoad: number): boolean => idLoad === id)) {
      this.stackingLoads = this.stackingLoads.filter((idLoad: number): boolean => idLoad !== id);
    } else {
      this.stackingLoads.push(id);
    }
    this.setLoadCheckboxIfValid();
    this.setStackingOptions();
  }

  private setLoadCheckboxIfValid(): void {
    const isLoadValid: boolean = this.form.get('load').valid && this.stackingLoads.length > 0;
    if (isLoadValid) {
      this.chooseLoadsToStack = true;
    }
  }

  public getColor(group: Group): string {
    return this.colorPipe.transform(group.color);
  }

  public onStacking(): void {
    if (this.lockStackingAndSupport) {
      return;
    }
    this.stacking = !this.stacking;
    this.setStackingOptions();
  }

  public onWeightOfFloors(): void {
    this.weightOfFloors = !this.weightOfFloors;
    this.setStackingOptions();
  }

  public onChooseLoadsToStack(): void {
    this.chooseLoadsToStack = !this.chooseLoadsToStack;
    this.setStackingOptions();
  }

  public onSpecifyPercentageOfSupport(): void {
    if (this.lockStackingAndSupport) {
      return;
    }
    this.specifyPercentageOfSupport = !this.specifyPercentageOfSupport;
    this.setStackingOptions();
  }

  private setPercentageCheckbox(): void {
    this.specifyPercentageOfSupport = true;
  }

  public setStackingOptions(): void {
    const isPipe: boolean = this.groupData.loadType === 2;
    const defaultFormation: number =
      this.defaultOptions?.formation !== undefined ? this.defaultOptions.formation : 0;
    const formation: number = isPipe ? (this.stacking ? 2 : 0) : defaultFormation;
    this.stackingOptions.emit({
      stacking: this.stacking,
      maxLoadsOnTop:
        this.stacking && +this.form.controls.stackQuantity.value > 0
          ? +this.form.controls.stackQuantity.value
          : null,
      boxesBelowAllowed: this.chooseLoadsToStack ? this.stackingLoads : null,
      maxWeightOnTop: this.weightOfFloors ? +this.form.controls.maxWeight.value : null,
      pctSupport: this.specifyPercentageOfSupport
        ? +this.form.controls.supportPercentage.value
        : null,
      alongFloor: !!this.form.controls.alongFloor.value,
      formation,
    });
  }

  private generateForm(): UntypedFormGroup {
    return new UntypedFormGroup({
      stackQuantity: new UntypedFormControl(this.defaultOptions?.maxLoadsOnTop),
      maxWeight: new UntypedFormControl(null),
      load: new UntypedFormControl(this.loadsGroups?.[0]?.id),
      supportPercentage: new UntypedFormControl(100),
      alongFloor: new UntypedFormControl(this.defaultOptions?.alongFloor),
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }
}
